/**
 * BookingBar.tsx
 * Renders a single MachineBooking bar inside the Machine Schedule grid.
 *
 * Styling (all tokens from theme.schedule.*):
 *   - Committed / WorkOrder  → committed (solid sage)
 *   - Quote / Tentative      → tentative + 45° hatch at 60% opacity
 *   - Maintenance            → maintenance (amber)
 *   - Calibration            → maintenance @70%
 *   - OutOfService           → noActivity (grey)
 *   - Manual (no event type) → machineTime (slate blue)
 *   - Other-plan reservation → noActivity (grey), solid, dimmed + lock glyph
 *     ("reserved in another plan" — show-the-blocker). This branch WINS over the
 *     tentative/source styling so a foreign draft's bar is unmistakably distinct.
 *
 * The bar is a *dumb* renderer: it exposes three drag targets and forwards
 * pointer-downs to the grid-level drag engine (useBookingDrag) via onBeginDrag:
 *   - left edge (6px)  → 'resize-start'
 *   - right edge (6px) → 'resize-end'
 *   - body             → 'move' (or a click when the pointer doesn't travel)
 * When this bar is the active drag it dims and ignores pointer events so the
 * floating <DragGhost> in the target row is the live preview.
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import type { MachineBookingCalendar } from '../../types/schedule';
import type { DragMode } from '../../hooks/useBookingDrag';
import { ConflictBadge, type ConflictType } from './ConflictBadge';
import { BookingAvatars } from './BookingAvatars';
import { bookingPrimaryLabel, bookingTestName } from '../../utils/bookingLabel';
import { isContinuousRun, CONTINUOUS_BADGE, CONTINUOUS_HINT } from '../../utils/runModeRendering';
import { isMergedPrepBooking, formatMinutesCompact } from '../../utils/prepBookingMerge';

/** Continuous (24/7) booking — non-event bar whose task snapshot says continuous. */
function isContinuousBooking(booking: MachineBookingCalendar): boolean {
  return !booking.EventType && isContinuousRun(booking.RunMode);
}

// ── helpers ────────────────────────────────────────────────────────────────

/** Derive the background color token for a booking. */
function getBarColor(booking: MachineBookingCalendar, theme: Theme): string {
  // "Reserved in another plan" wins over every other classification — a foreign
  // draft's tentative booking renders greyed (neutral grey = "no activity here
  // for THIS plan") so it reads as a locked blocker, not committed/tentative work.
  if (booking.isOtherScenario) return theme.schedule.noActivity;
  if (booking.EventType === 'OutOfService') return theme.schedule.noActivity;
  if (booking.EventType === 'Maintenance') return theme.schedule.maintenance;
  if (booking.EventType === 'Calibration') return alpha(theme.schedule.maintenance, 0.7);
  if (booking.Source === 'Quote') return theme.schedule.tentative;
  if (booking.Source === 'WorkOrder') return theme.schedule.committed;
  return theme.schedule.machineTime; // manual booking, no event type
}

/** Whether this booking renders with a hatched pattern (tentative/quote-sourced). */
function isHatched(booking: MachineBookingCalendar): boolean {
  // Other-plan reservations render as a solid dimmed (greyed) block, never
  // hatched — so they don't read as this scenario's own tentative proposals.
  if (booking.isOtherScenario) return false;
  return booking.Status === 'Tentative' || booking.Source === 'Quote';
}

/** Build a human-readable tooltip body (hover preview). */
function buildTooltipContent(booking: MachineBookingCalendar): React.ReactNode {
  const sourceLabel = booking.TaskSourceType ?? booking.Source;
  const dateRange = `${booking.StartDate} → ${booking.EndDate}`;
  const refNo =
    booking.WorkOrderNo
      ? `WO ${booking.WorkOrderNo}`
      : booking.QuoteNumber
        ? `Quote ${booking.QuoteNumber}`
        : null;
  const testName = bookingTestName(booking);
  // BUG-2026-0193: a merged prep bar names the folded steps + combined minutes
  // instead of a "Test:" line — it IS the work order's whole prep effort here.
  const merged = isMergedPrepBooking(booking);
  const mergedMinutes = merged && booking.TaskMachineMinutes != null
    ? ` · ${formatMinutesCompact(booking.TaskMachineMinutes)} combined`
    : '';
  return (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography variant="body2" fontWeight={700} gutterBottom>
        {bookingPrimaryLabel(booking)}
      </Typography>
      {merged ? (
        <Typography variant="caption" display="block">
          {testName}{mergedMinutes} — {booking.mergedPrepBookings.length} prep bookings shown as one bar
        </Typography>
      ) : testName && (
        <Typography variant="caption" display="block">
          Test: {testName}
        </Typography>
      )}
      {booking.ProjectName && (
        <Typography variant="caption" display="block">
          Project: {booking.ProjectName}
        </Typography>
      )}
      {booking.ClientName && (
        <Typography variant="caption" display="block">
          Client: {booking.ClientName}
        </Typography>
      )}
      <Typography variant="caption" display="block">
        {booking.MachineName} ({booking.MachineCode})
      </Typography>
      <Typography variant="caption" display="block">
        {dateRange}
      </Typography>
      {isContinuousBooking(booking) && (
        <Typography variant="caption" display="block" fontWeight={700} sx={{ mt: 0.5 }}>
          {CONTINUOUS_HINT}
        </Typography>
      )}
      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
        Status: {booking.Status} | Source: {sourceLabel}
      </Typography>
      {booking.isOtherScenario && (
        <Typography
          variant="caption"
          display="block"
          fontWeight={700}
          sx={{ mt: 0.5 }}
        >
          Reserved in another plan — blocks this slot. Drop here to overwrite.
        </Typography>
      )}
      {refNo && (
        <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
          {refNo}
        </Typography>
      )}
      {booking.Assignees.length > 0 && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {booking.Assignees.map((a) => a.Name).join(', ')}
        </Typography>
      )}
      {booking.Notes && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
          {booking.Notes}
        </Typography>
      )}
      <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
        {merged ? 'Click for details · drag to move all prep together' : 'Click for details · drag to move/resize'}
      </Typography>
    </Box>
  );
}

// ── component ──────────────────────────────────────────────────────────────

interface BookingBarProps {
  booking: MachineBookingCalendar;
  /** Left offset as a fraction (0–1) of the visible day window. */
  leftFraction: number;
  /** Width as a fraction (0–1) of the visible day window. */
  widthFraction: number;
  /** Conflict glyph: 'hard' (overlap) | 'soft' (missing capability) | none. */
  conflictType?: ConflictType | null;
  /** True while this bar is the active drag — dim it; the ghost is the preview. */
  isActiveDrag?: boolean;
  /** True when this bar is the keyboard-selected booking (selection ring). M7. */
  selected?: boolean;
  /** Rendered pixel width of the bar — drives whether assignee avatars fit. M6. */
  pxWidth?: number;
  /**
   * BUG-2026-0132 — when true this bar is NOT this scenario's to move (another
   * project's committed work, another draft's tentative hold, or a manual event).
   * It renders as a dimmed, locked blocker (visible for context) and all drag
   * gestures are no-ops. Default false (fully draggable, e.g. the standalone
   * Machine Schedule editor).
   */
  readOnly?: boolean;
  /** Start a drag in the given mode (grid owns the gesture). */
  onBeginDrag: (booking: MachineBookingCalendar, mode: DragMode, e: React.PointerEvent) => void;
}

export const BookingBar: React.FC<BookingBarProps> = ({
  booking,
  leftFraction,
  widthFraction,
  conflictType,
  isActiveDrag = false,
  selected = false,
  pxWidth = 0,
  readOnly = false,
  onBeginDrag,
}) => {
  const theme = useTheme();
  // "Locked" = a foreign reservation (other scenario's hold) OR explicitly marked
  // read-only by the parent (BUG-2026-0132: other projects' bars in the scenario
  // planning grid). Locked bars are dimmed, show the not-allowed cursor + lock
  // icon, and (when readOnly) ignore every drag gesture.
  const otherPlan = Boolean(booking.isOtherScenario);
  const locked = otherPlan || readOnly;
  const barColor = getBarColor(booking, theme);
  const hatched = isHatched(booking);
  const showAvatars = pxWidth > 70 && booking.Assignees.length > 0;
  // Run Mode Phase C: continuous (24/7) bars carry a distinct heavier border in
  // the continuous token + a "24/7" mini-badge (when the bar is wide enough),
  // so a bar running through a weekend reads as deliberately RUNNING there.
  const continuous = isContinuousBooking(booking);
  const showContinuousBadge = continuous && pxWidth > 46;
  // BUG-2026-0193: a merged prep bar (N per-line prep bookings folded into one)
  // moves as a unit but has no meaningful edge to resize — hide the handles.
  const mergedPrep = isMergedPrepBooking(booking);
  const resizable = !readOnly && !mergedPrep;

  const barStyle: React.CSSProperties = {
    position: 'absolute',
    top: '4px',
    bottom: '4px',
    left: `${leftFraction * 100}%`,
    width: `${widthFraction * 100}%`,
    borderRadius: 5,
    backgroundColor: hatched ? 'transparent' : barColor,
    // 2026-07 sticky-headers UX pass: a whisper of depth lifts bars off the
    // day-cell lattice (matches the approved mockup).
    boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.15)}`,
    border: continuous
      ? `2px ${locked ? 'dashed' : 'solid'} ${theme.schedule.continuous}`
      : `1px ${locked ? 'dashed' : 'solid'} ${hatched ? alpha(barColor, 0.8) : barColor}`,
    outline: selected ? `2px solid ${theme.schedule.selection}` : undefined,
    outlineOffset: selected ? '1px' : undefined,
    overflow: 'visible',
    // Locked reservations aren't this scenario's to move — show the lock
    // cursor and dim them so they read as a visible-but-not-editable blocker.
    cursor: isActiveDrag ? 'grabbing' : locked ? 'not-allowed' : 'grab',
    opacity: isActiveDrag ? 0.35 : locked ? 0.5 : 1,
    pointerEvents: isActiveDrag ? 'none' : 'auto',
    // Draggable bars ALWAYS layer above locked blockers. A duplicated scenario
    // renders the original draft's holds as locked twins at the exact same
    // slots; when a twin happened to come later in fetch order it covered the
    // own bar and every hover/drag hit the 🚫 blocker instead (drag "stopped
    // working" on copied plans — 2026-07-24).
    zIndex: locked ? 1 : 2,
    display: 'flex',
    alignItems: 'center',
    minWidth: 4,
    boxSizing: 'border-box',
    touchAction: 'none',
  };

  const innerClipStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    borderRadius: 4,
  };

  const hatchStyle: React.CSSProperties | undefined = hatched
    ? {
        position: 'absolute',
        inset: 0,
        backgroundImage: [
          `repeating-linear-gradient(`,
          `  45deg,`,
          `  ${alpha(barColor, 0.6)} 0px,`,
          `  ${alpha(barColor, 0.6)} 4px,`,
          `  ${alpha(barColor, 0.15)} 4px,`,
          `  ${alpha(barColor, 0.15)} 8px`,
          `)`,
        ].join(''),
        borderRadius: 2,
      }
    : undefined;

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    // Hatched (tentative) bars draw the label in the bar colour over a busy 45°
    // hatch, which was hard to read. A light paper halo + heavier weight lifts the
    // text off the pattern (and off the non-work veil) without a solid label chip.
    fontWeight: hatched ? 700 : 600,
    color: hatched ? barColor : theme.palette.getContrastText(barColor),
    textShadow: hatched
      ? `0 0 2px ${theme.palette.background.paper}, 0 0 3px ${theme.palette.background.paper}, 0 0 4px ${theme.palette.background.paper}`
      : undefined,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingLeft: 7,
    paddingRight: showAvatars ? Math.min(3, booking.Assignees.length) * 14 + 14 : 7,
    position: 'relative',
    zIndex: 2,
    pointerEvents: 'none',
  };

  const edgeHandle = (side: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    [side]: 0,
    width: 6,
    cursor: 'ew-resize',
    zIndex: 4,
    backgroundColor: 'transparent',
    touchAction: 'none',
  });

  // The bar shows the WORK ORDER's descriptive name (WO number in brackets); the test
  // name lives in the detail drawer now (shared helper keeps every view consistent).
  // A merged prep bar appends "· Prep ×N" so it reads as the folded prep block.
  const label = mergedPrep
    ? `${bookingPrimaryLabel(booking)} · Prep ×${booking.mergedPrepBookings.length}`
    : bookingPrimaryLabel(booking);

  return (
    <Tooltip
      title={buildTooltipContent(booking)}
      placement="top"
      arrow
      enterDelay={400}
      disableHoverListener={isActiveDrag}
    >
      <div
        style={barStyle}
        data-booking-key={booking.MachineBookingKey}
        data-testid="booking-bar"
        data-readonly={readOnly ? 'true' : undefined}
        data-prep-merge-count={mergedPrep ? booking.mergedPrepBookings.length : undefined}
        onPointerDown={(e) => { if (readOnly) return; onBeginDrag(booking, 'move', e); }}
      >
        <div style={innerClipStyle}>
          {hatchStyle && <div style={hatchStyle} />}
          <span style={labelStyle}>
            {locked && (
              <LockOutlinedIcon
                sx={{ fontSize: 11, mr: 0.4, verticalAlign: 'text-bottom' }}
              />
            )}
            {/* 24/7 mini-badge — continuous runs (Run Mode Phase C). */}
            {showContinuousBadge && (
              <span
                style={{
                  display: 'inline-block',
                  padding: '0 3px',
                  marginRight: 4,
                  borderRadius: 2,
                  fontSize: 8,
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  lineHeight: '12px',
                  backgroundColor: theme.schedule.continuous,
                  color: theme.palette.getContrastText(theme.schedule.continuous),
                }}
              >
                {CONTINUOUS_BADGE}
              </span>
            )}
            {label}
          </span>
        </div>

        {/* Assignee avatars (right end, when the bar is wide enough) */}
        {showAvatars && (
          <Box
            sx={{
              position: 'absolute',
              right: 9,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              pointerEvents: 'none',
            }}
          >
            <BookingAvatars assignees={booking.Assignees} max={pxWidth > 120 ? 3 : 2} />
          </Box>
        )}

        {/* Resize handles — none on a merged prep bar (moves as a unit only). */}
        {!mergedPrep && (
          <>
            <div
              style={resizable ? edgeHandle('left') : { ...edgeHandle('left'), cursor: 'not-allowed' }}
              onPointerDown={(e) => { if (!resizable) return; onBeginDrag(booking, 'resize-start', e); }}
              data-testid="booking-bar-resize-start"
              title={resizable ? 'Drag to change start' : undefined}
            />
            <div
              style={resizable ? edgeHandle('right') : { ...edgeHandle('right'), cursor: 'not-allowed' }}
              onPointerDown={(e) => { if (!resizable) return; onBeginDrag(booking, 'resize-end', e); }}
              data-testid="booking-bar-resize-end"
              title={resizable ? 'Drag to change end' : undefined}
            />
          </>
        )}

        {conflictType && <ConflictBadge type={conflictType} />}
      </div>
    </Tooltip>
  );
};
